// animated background patterns on one canvas under the fog. a few dozen particles at ~30fps, drawn in the
// theme's own colours. hidden tab, calm motion or reduced motion leaves a single still frame instead

import { reducedMotion } from "./motion_pref";
import { sceneOwner } from "./ambient_scenes";

type Kind ="starfield" | "leaves" | "petals" | "snow" | "rain" | "fireflies" | "embers" | "bubbles" | "matrix";

export const AMBIENT_KINDS: Kind[] = ["starfield", "leaves", "petals", "snow", "rain", "fireflies", "embers", "bubbles", "matrix"];

interface Bit {
  x: number;
  y: number;
  size: number;
  speed: number;
  phase: number;
  spin: number;
  hue: number;
}

interface Palette {
  ink: string;
  dim: string;
  chi: string;
  warm: string;
  green: string;
  // white snow vanishes on a light theme, so it falls in the soft ink there
  snow: string;
}

const FRAME_MS = 1000 / 30;

function isKind(id: string): id is Kind {
  return (AMBIENT_KINDS as string[]).includes(id);
}

function palette(): Palette {
  const css = getComputedStyle(document.documentElement);
  const read = (name: string) => css.getPropertyValue(name).trim() || "#888";
  const light = document.documentElement.dataset.tone === "light";
  return { ink: read("--ink"), dim: read("--ink-3"), chi: read("--chi"), warm: read("--amber"), green: read("--green"), snow: read(light ? "--ink-3" : "--ink") };
}

// one particle per this many square pixels, so a phone gets a handful and a desktop a few dozen
const DENSITY: Record<Kind, number> = { starfield: 3200, leaves: 16000, petals: 12000, snow: 4500, rain: 5000, fireflies: 18000, embers: 14000, bubbles: 22000, matrix: 9000 };

const RISING: Kind[] = ["embers", "bubbles"];
const GLYPH_PX = 14;

function spawn(kind: Kind, width: number, height: number, anywhere: boolean): Bit {
  const rand = Math.random;
  const rising = RISING.includes(kind);
  return {
    x: kind === "matrix" ? Math.floor((rand() * width) / GLYPH_PX) * GLYPH_PX : rand() * width,
    y: anywhere ? rand() * height : rising ? height + 20 + rand() * 40 : -20 - rand() * 40,
    size: kind === "starfield" ? 0.6 + rand() * 1.3 : kind === "snow" ? 0.8 + rand() * 1.8 : kind === "rain" ? 10 + rand() * 10 : kind === "fireflies" ? 1.2 + rand() * 1.4 : kind === "petals" ? 5 + rand() * 4 : kind === "embers" ? 0.8 + rand() * 1.7 : kind === "bubbles" ? 3 + rand() * 9 : kind === "matrix" ? GLYPH_PX : 6 + rand() * 5,
    speed: kind === "rain" ? 380 + rand() * 220 : kind === "snow" ? 14 + rand() * 22 : kind === "fireflies" ? 6 + rand() * 10 : kind === "starfield" ? 0.6 + rand() * 1.6 : kind === "embers" ? 22 + rand() * 38 : kind === "bubbles" ? 14 + rand() * 26 : kind === "matrix" ? 70 + rand() * 110 : 18 + rand() * 22,
    phase: rand() * Math.PI * 2,
    spin: (rand() - 0.5) * 2,
    hue: rand()
  };
}

function drawLeaf(ctx: CanvasRenderingContext2D, bit: Bit, color: string, petal: boolean): void {
  ctx.save();
  ctx.translate(bit.x, bit.y);
  ctx.rotate(bit.phase * bit.spin);
  ctx.fillStyle = color;
  ctx.beginPath();
  if (petal) {
    // a little notched cherry petal
    ctx.moveTo(0, -bit.size * 0.9);
    ctx.quadraticCurveTo(bit.size * 0.8, -bit.size * 0.2, 0, bit.size);
    ctx.quadraticCurveTo(-bit.size * 0.8, -bit.size * 0.2, 0, -bit.size * 0.9);
  } else {
    ctx.ellipse(0, 0, bit.size * 0.45, bit.size, 0, 0, Math.PI * 2);
  }
  ctx.fill();
  if (!petal) {
    ctx.strokeStyle = color;
    ctx.globalAlpha *= 0.6;
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(0, -bit.size);
    ctx.lineTo(0, bit.size * 1.4);
    ctx.stroke();
  }
  ctx.restore();
}

function step(kind: Kind, bit: Bit, dt: number, width: number, height: number): void {
  bit.phase += dt * (kind === "starfield" ? bit.speed : 1);
  if (kind === "starfield") return;
  if (kind === "fireflies") {
    bit.x += Math.cos(bit.phase * 0.7 + bit.hue * 6) * bit.speed * dt;
    bit.y += Math.sin(bit.phase * 0.5 + bit.hue * 4) * bit.speed * dt;
    if (bit.x < -10) bit.x = width + 10;
    if (bit.x > width + 10) bit.x = -10;
    if (bit.y < -10) bit.y = height + 10;
    if (bit.y > height + 10) bit.y = -10;
    return;
  }
  if (RISING.includes(kind)) {
    bit.y -= bit.speed * dt;
    bit.x += Math.sin(bit.phase * 1.3) * (kind === "embers" ? 16 : 10) * dt;
    if (bit.y < -20) Object.assign(bit, spawn(kind, width, height, false));
    return;
  }
  bit.y += bit.speed * dt;
  if (kind === "matrix" && bit.y > height + GLYPH_PX * 8) {
    Object.assign(bit, spawn(kind, width, height, false));
    return;
  }
  if (kind === "matrix") return;
  if (kind === "rain") bit.x += bit.speed * 0.18 * dt;
  else bit.x += Math.sin(bit.phase * 1.3) * (kind === "snow" ? 8 : 22) * dt;
  if (bit.y > height + 20 || bit.x > width + 30) Object.assign(bit, spawn(kind, width, height, false));
}

function paint(ctx: CanvasRenderingContext2D, kind: Kind, bits: Bit[], colors: Palette, shooting: { x: number; y: number; life: number } | null): void {
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  for (const bit of bits) {
    if (kind === "starfield") {
      ctx.globalAlpha = 0.25 + 0.55 * (0.5 + 0.5 * Math.sin(bit.phase));
      ctx.fillStyle = bit.hue > 0.85 ? colors.chi : colors.ink;
      ctx.fillRect(bit.x, bit.y, bit.size, bit.size);
      if (bit.size > 1.3) {
        // the bright ones get a tiny cross like a psx skybox
        ctx.globalAlpha *= 0.5;
        ctx.fillRect(bit.x - bit.size, bit.y + bit.size / 2 - 0.3, bit.size * 3, 0.6);
        ctx.fillRect(bit.x + bit.size / 2 - 0.3, bit.y - bit.size, 0.6, bit.size * 3);
      }
    } else if (kind === "leaves" || kind === "petals") {
      ctx.globalAlpha = 0.75;
      const color = kind === "petals" ? colors.chi : bit.hue > 0.5 ? colors.warm : bit.hue > 0.2 ? colors.chi : colors.green;
      drawLeaf(ctx, bit, color, kind === "petals");
    } else if (kind === "snow") {
      ctx.globalAlpha = 0.75;
      ctx.fillStyle = colors.snow;
      ctx.beginPath();
      ctx.arc(bit.x, bit.y, bit.size, 0, Math.PI * 2);
      ctx.fill();
    } else if (kind === "embers") {
      const glow = 0.5 + 0.5 * Math.sin(bit.phase * 3 + bit.hue * 9);
      ctx.globalAlpha = 0.25 + 0.6 * glow;
      ctx.fillStyle = bit.hue > 0.5 ? colors.warm : colors.chi;
      ctx.shadowColor = ctx.fillStyle;
      ctx.shadowBlur = 6;
      ctx.beginPath();
      ctx.arc(bit.x, bit.y, bit.size, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
    } else if (kind === "bubbles") {
      ctx.globalAlpha = 0.5;
      ctx.strokeStyle = colors.chi;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(bit.x, bit.y, bit.size, 0, Math.PI * 2);
      ctx.stroke();
      ctx.globalAlpha = 0.35;
      ctx.beginPath();
      ctx.arc(bit.x - bit.size * 0.35, bit.y - bit.size * 0.35, Math.max(0.6, bit.size * 0.18), 0, Math.PI * 2);
      ctx.fillStyle = colors.ink;
      ctx.fill();
    } else if (kind === "matrix") {
      ctx.font = `${GLYPH_PX}px "DotGothic16", monospace`;
      ctx.textBaseline = "top";
      for (let trail = 0; trail < 8; trail++) {
        const code = 0x30a1 + ((Math.floor(bit.phase * 5) * (trail % 2 ? 1 : 0) + trail * 7 + Math.floor(bit.hue * 50)) % 80);
        ctx.globalAlpha = trail === 0 ? 0.95 : 0.55 * (1 - trail / 8);
        ctx.fillStyle = trail === 0 ? colors.ink : colors.chi;
        ctx.fillText(String.fromCharCode(code), bit.x, bit.y - trail * GLYPH_PX);
      }
    } else if (kind === "rain") {
      ctx.globalAlpha = 0.6;
      ctx.strokeStyle = colors.ink;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(bit.x, bit.y);
      ctx.lineTo(bit.x - bit.size * 0.18, bit.y - bit.size);
      ctx.stroke();
    } else {
      const glow = 0.5 + 0.5 * Math.sin(bit.phase * 2.2 + bit.hue * 9);
      ctx.globalAlpha = 0.15 + 0.6 * glow;
      ctx.fillStyle = colors.chi;
      ctx.shadowColor = colors.chi;
      ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.arc(bit.x, bit.y, bit.size, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
    }
  }
  if (shooting) {
    ctx.globalAlpha = Math.max(0, shooting.life);
    ctx.strokeStyle = colors.ink;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(shooting.x, shooting.y);
    ctx.lineTo(shooting.x - 70, shooting.y - 28);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

function sized(canvas: HTMLCanvasElement): { ctx: CanvasRenderingContext2D; width: number; height: number } | null {
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  const ratio = Math.min(window.devicePixelRatio || 1, 2);
  const width = canvas.clientWidth || 1;
  const height = canvas.clientHeight || 1;
  canvas.width = Math.round(width * ratio);
  canvas.height = Math.round(height * ratio);
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  return { ctx, width, height };
}

// a still frame for the pattern picker swatches
export function paintAmbientStill(canvas: HTMLCanvasElement, id: string): void {
  if (!isKind(id)) return;
  const surface = sized(canvas);
  if (!surface) return;
  const count = Math.max(4, Math.round((surface.width * surface.height) / DENSITY[id] * 1.6));
  const bits = Array.from({ length: count }, () => spawn(id, surface.width, surface.height, true));
  paint(surface.ctx, id, bits, palette(), null);
}

function stillWanted(): boolean {
  return document.hidden || reducedMotion();
}

let running: { kind: Kind; stop: () => void } | null = null;

export function runAmbient(id: string): void {
  const canvas = document.querySelector<HTMLCanvasElement>("canvas.ambient");
  if (!canvas) return;
  if (running?.kind === id) return;
  running?.stop();
  running = null;
  // no pattern of its own: the canvas is free for the station scenes
  sceneOwner(isKind(id));
  if (!isKind(id)) {
    canvas.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
    return;
  }
  const kind = id;
  let surface = sized(canvas);
  if (!surface) return;
  let colors = palette();
  const fill = () => Array.from({ length: Math.round((surface!.width * surface!.height) / DENSITY[kind]) }, () => spawn(kind, surface!.width, surface!.height, true));
  let bits = fill();
  let shooting: { x: number; y: number; life: number } | null = null;
  let frame = 0;
  let last = 0;

  const tick = (now: number) => {
    frame = requestAnimationFrame(tick);
    if (now - last < FRAME_MS) return;
    const dt = Math.min(0.1, (now - last) / 1000);
    last = now;
    for (const bit of bits) step(kind, bit, dt, surface!.width, surface!.height);
    if (kind === "starfield") {
      if (!shooting && Math.random() < dt / 9) shooting = { x: Math.random() * surface!.width, y: Math.random() * surface!.height * 0.4, life: 1 };
      if (shooting) {
        shooting.x += 520 * dt;
        shooting.y += 208 * dt;
        shooting.life -= dt * 1.6;
        if (shooting.life <= 0) shooting = null;
      }
    }
    paint(surface!.ctx, kind, bits, colors, shooting);
  };
  const settle = () => {
    cancelAnimationFrame(frame);
    if (stillWanted()) paint(surface!.ctx, kind, bits, colors, null);
    else {
      last = performance.now();
      frame = requestAnimationFrame(tick);
    }
  };
  const resize = () => {
    surface = sized(canvas);
    if (!surface) return;
    bits = fill();
    settle();
  };
  // the theme changed under us, pick up its colours
  const recolor = () => {
    colors = palette();
    if (stillWanted()) paint(surface!.ctx, kind, bits, colors, null);
  };
  const watch = new MutationObserver(recolor);
  watch.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme", "style"] });
  const motion = matchMedia("(prefers-reduced-motion: reduce)");
  document.addEventListener("visibilitychange", settle);
  window.addEventListener("resize", resize);
  motion.addEventListener("change", settle);
  const calmWatch = new MutationObserver(settle);
  calmWatch.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
  settle();

  running = {
    kind,
    stop: () => {
      cancelAnimationFrame(frame);
      watch.disconnect();
      calmWatch.disconnect();
      document.removeEventListener("visibilitychange", settle);
      window.removeEventListener("resize", resize);
      motion.removeEventListener("change", settle);
      canvas.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
    }
  };
}
